import { useContext } from 'react';
import { useStyletron } from 'baseui';
import { Badge, SHAPE } from 'baseui/badge';
import { nanoid } from 'nanoid';
import { UserIcon } from '@gtintel/platform-atoms';
import { NPRequestStatus } from '@gtintel/platform-types';
import { COLOURS, hexToRGB } from '@gtintel/platform-utils';
import DueDatePill from '../../../components/DueDatePill';
import { RequestStatusPill } from '../../../components/RequestStatusIcon';
import { ProjectContext, RequestsContext } from '../../context';
import Actions from './Actions';
export const RequestListItem = ({
  id,
  onItemClick,
  setIsOpen,
  setModalAssignmentId
}) => {
  const {
    currency
  } = useContext(ProjectContext);
  const {
    get
  } = useContext(RequestsContext);
  const [css] = useStyletron();
  const item = get(id);
  if (!item) {
    return <div className={css({
      cursor: 'pointer',
      background: 'white',
      border: '1px solid #e2e2e2',
      borderRadius: '4px',
      display: 'flex',
      flexFlow: 'row nowrap',
      justifyContent: 'space-between',
      marginBottom: '14px',
      padding: '14px',
      transition: 'all 250ms ease-in',
      width: '100%',
      ':hover': {
        borderColor: '#d1d1d1'
      },
      '@media screen and (max-width: 768px)': {
        flexDirection: 'column',
        gap: '24px'
      }
    })} data-testid="no-matching-request">
        No Request found with ID: {id}
      </div>;
  }
  const borderColor = item.is_lead_partner_assignment || item.is_qa_assignment ? item.is_lead_partner_assignment ? COLOURS.gold : COLOURS.blue : COLOURS.borderGray;
  const hoverBorderColor = item.is_lead_partner_assignment || item.is_qa_assignment ? item.is_lead_partner_assignment ? hexToRGB(COLOURS.gold, 0.5) : hexToRGB(COLOURS.blue, 0.5) : COLOURS.darkBorderGray;
  const component = <section key={nanoid()} className={css({
    background: 'white',
    border: `2px solid ${borderColor}`,
    borderRadius: '4px',
    display: 'flex',
    flexFlow: 'row',
    justifyContent: 'space-between',
    marginBottom: '14px',
    transition: 'all 250ms ease-in',
    width: '100%',
    ':hover': {
      borderColor: hoverBorderColor
    },
    '@media screen and (max-width: 768px)': {
      flexFlow: 'column',
      gap: '24px'
    }
  })}>
      <div className={css({
      cursor: 'pointer',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      padding: '14px',
      width: '100%',
      '@media screen and (max-width: 768px)': {
        gap: '24px',
        gridTemplateColumns: '1fr'
      }
    })} role="button" tabIndex={0} data-testid="list-item-button" onClick={() => onItemClick(item.id)} onKeyUp={e => {
      if (e.key === 'Enter') {
        onItemClick(item.id);
      }
    }}>
        <div className={css({
        alignItems: 'flex-start',
        display: 'flex',
        flexFlow: 'column',
        gap: '8px'
      })}>
          <small className={css({
          color: '#808080'
        })}>
            {item.name ?? `Assignment #${item.id}`}
          </small>
          <div className={css({
          alignItems: 'center',
          display: 'flex',
          flexFlow: 'row nowrap',
          gap: '8px'
        })}>
            <RequestStatusPill status={item.status} showIcon={false} />
            <UserIcon user={item.partner} showName={true} />
          </div>
        </div>

        <div className={css({
        display: 'flex',
        flexFlow: 'column',
        gap: '8px',
        justifyContent: 'flex-end'
      })}>
          <DueDatePill dueDate={item.due_date} submittedDate={item.submitted_date} />
          {item.fee ? <p className={css({
          margin: 0
        })}>
              <strong>Fee:</strong> {currency()}
              {item.fee}
            </p> : <strong>No fee set!</strong>}
        </div>
      </div>

      <div className={css({
      alignItems: 'flex-end',
      display: 'flex',
      flexFlow: 'column',
      gap: '8px',
      justifyContent: 'flex-end',
      minWidth: 'fit-content',
      padding: '14px',
      '@media screen and (max-width: 768px)': {
        alignItems: 'flex-start',
        justifyContent: 'flex-start'
      }
    })}>
        <Actions id={item.id} canDelete={item.status === NPRequestStatus.Draft} status={item.status} setIsOpen={setIsOpen} setModalAssignmentId={setModalAssignmentId} isLeadPartnerAssignment={item.is_lead_partner_assignment} />
        <p className={css({
        margin: '0'
      })}>
          {item.components.length} scope items selected
        </p>
      </div>
    </section>;
  return item.is_lead_partner_assignment || item.is_qa_assignment ? <Badge content={item.is_lead_partner_assignment ? 'Lead Partner Assignment' : 'QA Assignment'} shape={SHAPE.rectangle} verticalOffset="-8px" horizontalOffset="-8px" overrides={{
    Root: {
      style: {
        width: '100%'
      }
    },
    Badge: {
      style: {
        backgroundColor: item.is_lead_partner_assignment ? COLOURS.gold : COLOURS.blue
      }
    }
  }}>
      {component}
    </Badge> : component;
};